import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { RoundButton } from 'ui/Buttons';
import { ratingRequestAvailable } from 'shared/functions';
import {
  requestEntityRatingAction,
  requestSigEntityRatingAction,
} from 'store/entity/entityActions';
import { requestInstrumentRatingAction } from 'store/instrument/instrumentActions';
import { orderTypes } from 'shared/constants/orderTypes';
import { requestRatingNewsAction } from 'store/newsFeed/newsFeedActions';
import { usePrevious } from 'shared/hooks';
import { newsArtifactsTypes } from 'shared/constants/artifactTypes';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { Tooltip } from '@mui/material';
import { useConfirmedRequest } from 'shared/hooks/useConfirmedRequest/useConfirmedRequest';

const RequestButton = ({
  id,
  orderType,
  product,
  reset,
  alreadyRequested,
  ratingType,
  disabled,
  tooltip,
  className,
  confirmationModalOpen,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [requested, setRequested] = useState(false);

  const previousReset = usePrevious(reset);

  useEffect(() => {
    if (reset !== previousReset && reset && !alreadyRequested) {
      setRequested(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset]);

  const handleRequestRating = () => {
    let action = null;
    if (orderType === orderTypes.SIG_ENTITY) {
      const { organisationId, userId } = product;
      action = requestSigEntityRatingAction({ ids: [id], organisationId, userId });
    } else if ([orderTypes.INSTRUMENT, orderTypes.ENTITY].includes(orderType)) {
      const level = product.l5 && product.l5.available ? 'L5' : 'L4';
      const requestActionCreator =
        orderType === orderTypes.INSTRUMENT
          ? requestInstrumentRatingAction
          : requestEntityRatingAction;
      action = requestActionCreator({ id, level });
    } else if (Object.keys(newsArtifactsTypes).indexOf(orderType) >= 0) {
      action = requestRatingNewsAction({ id, type: orderType });
    }

    if (action) {
      setRequested(true);
      dispatch(action);
    }
  };

  const { scheduleRequest } = useConfirmedRequest({
    handleRequest: handleRequestRating,
    confirmationModalOpen,
  });

  const handleRequestRatingClick = () => {
    if (orderType === orderTypes.SIG_ENTITY) {
      scheduleRequest();
    } else {
      handleRequestRating();
    }
  };

  const isRequestAvailable =
    !alreadyRequested &&
    !requested &&
    (Object.keys(newsArtifactsTypes).indexOf(orderType) >= 0 ||
      ratingRequestAvailable(product, ratingType));

  const renderButton = (
    <div {...(className && { className })}>
      <RoundButton
        color="success"
        onClick={handleRequestRatingClick}
        disableRipple={!isRequestAvailable}
        disabled={!isRequestAvailable || disabled}
        label={isRequestAvailable ? t('Request') : t('Requested')}
      />
    </div>
  );

  return tooltip ? <Tooltip title={tooltip}>{renderButton}</Tooltip> : renderButton;
};

RequestButton.defaultProps = {
  product: null,
  reset: false,
  alreadyRequested: false,
  ratingType: ratingTypes.CREDIT_RATING,
  disabled: false,
  className: undefined,
  tooltip: '',
  confirmationModalOpen: () => null,
};

RequestButton.propTypes = {
  id: PropTypes.string.isRequired,
  orderType: PropTypes.string.isRequired,
  product: PropTypes.object,
  reset: PropTypes.bool,
  alreadyRequested: PropTypes.bool,
  ratingType: PropTypes.oneOf(Object.values(ratingTypes)),
  disabled: PropTypes.bool,
  tooltip: PropTypes.string,
  className: PropTypes.string,
  confirmationModalOpen: PropTypes.func,
};

export default RequestButton;
